import { computed, ref, type ComputedRef, type Ref } from 'vue'
import type { Technician } from '@/api/orders'
import { postCareOrderMeta } from '@/api/orders'
import { stripTechnicianNoPrefix } from '@/utils/technicianNoFromList'
import { getScheduleInspectorData } from '@/utils/scheduleInspectorCache'
import { inspectorVisualsForIndex } from '../utils/scheduleInspectorRows'
import type { InspectorRow } from '../types'

export type ScheduleTechnician = Technician & { staffNo?: string; linkedIds?: string[] }

export function dedupeTechniciansByNameAndStaffNo(
  list: (Technician & { staffNo?: string })[],
): ScheduleTechnician[] {
  const byKey = new Map<string, ScheduleTechnician>()
  for (const t of list) {
    const name = t.name.trim()
    if (!name) continue
    const staffNo = stripTechnicianNoPrefix(t.staffNo) ?? ''
    const key = `${name.toLowerCase()}|${staffNo.toLowerCase()}`
    const existing = byKey.get(key)
    if (!existing) {
      byKey.set(key, { ...t, name, staffNo: staffNo || undefined, linkedIds: [t.id] })
      continue
    }
    const ids = new Set([...(existing.linkedIds ?? [existing.id]), t.id])
    existing.linkedIds = [...ids]
  }
  return [...byKey.values()]
}

export interface UseCareScheduleInspectorsParams {
  careMeta: Ref<import('@/api/orders').CareOrderMetaData | null>
  technicianNoLookup: Ref<import('@/utils/technicianNoFromList').TechnicianNoLookup | null>
  /** Delegated from main orchestration (large cross-cutting logic). */
  rebuildInspectorAssignmentRows: () => void
  finishInspectorSelectionAfterLoad: () => void
  applyRescheduleOrderContext: (orderId: string) => void
  isRescheduleMode: ComputedRef<boolean>
  isFollowUpSchedulePageActive: () => boolean
  selectedOrderId: Ref<string>
  resetSchedulePickerForUnassignedOrder: (orderId: string) => void
  syncInspectorSidebarSelectionForScheduleOrder: (orderId: string, lineIndex: number) => void
  scheduleTargetLineIndexForOrder: (orderId: string) => number
  assignmentsByOrderId: Ref<Record<string, import('../types').OrderScheduleAssignment>>
  scheduleClearedByUser: Ref<Set<string>>
  selectInspectorImpl: (id: string) => void
  syncAssignmentUiForSelectedOrder: () => void
  hideToast: () => void
  clearInspectorSidebarSelection: () => void
}

export function useCareScheduleInspectors(params: UseCareScheduleInspectorsParams) {
  const inspectors = ref<InspectorRow[]>([])
  const selectedInspectorId = ref('')
  const expandedInspectorIds = ref<Set<string>>(new Set())
  const inspectorSidebarSelectedOrderId = ref('')
  const inspectorSidebarSelectedLineIndex = ref<number | null>(null)

  function inspectorRowById(inspectorId: string): InspectorRow | undefined {
    const key = String(inspectorId)
    return inspectors.value.find((ins) => (ins.linkedIds ?? [ins.id]).some((id) => String(id) === key))
  }

  function isSameInspectorId(a: string, b: string): boolean {
    const rowA = inspectorRowById(a)
    const rowB = inspectorRowById(b)
    if (!rowA || !rowB) return String(a) === String(b)
    return rowA.id === rowB.id
  }

  function applyInspectorRowsFromTechnicians(list: ScheduleTechnician[]): void {
    inspectors.value = list.map((t, i) => {
      const { dotIcon, pinIcon, visualTone } = inspectorVisualsForIndex(i)
      return {
        id: t.id,
        name: t.name,
        staffNo: stripTechnicianNoPrefix(t.staffNo),
        linkedIds: t.linkedIds ?? [t.id],
        visualTone,
      orderCount: 0,
      dotIcon,
        pinIcon,
        assignments: [],
      }
    })
  }

  async function loadInspectors() {
    const { lookup, rows } = await getScheduleInspectorData()
    params.technicianNoLookup.value = lookup

    const fromList = dedupeTechniciansByNameAndStaffNo(rows)
    if (fromList.length) {
      applyInspectorRowsFromTechnicians(fromList)
      params.finishInspectorSelectionAfterLoad()
      return
    }

    try {
      params.careMeta.value = await postCareOrderMeta()
      const fromMeta = dedupeTechniciansByNameAndStaffNo(
        (params.careMeta.value?.technicians ?? [])
          .filter((t) => Number(t.value) !== 0)
          .map((t) => ({
            id: String(t.value),
            name: String(t.label),
            staffNo:
              stripTechnicianNoPrefix(lookup?.byId.get(String(t.value))) ??
              stripTechnicianNoPrefix(t.staff_no),
          })),
      )
      applyInspectorRowsFromTechnicians(fromMeta.length ? fromMeta : [])
    } catch {
      params.careMeta.value = null
      inspectors.value = []
    }

    params.finishInspectorSelectionAfterLoad()
  }

  function ensureInspectorExpanded(inspectorId: string) {
    const key = String(inspectorRowById(inspectorId)?.id ?? inspectorId)
    if (!key) return
    expandedInspectorIds.value = new Set([key])
    selectedInspectorId.value = key
  }

  function isInspectorExpanded(id: string): boolean {
    const key = String(inspectorRowById(id)?.id ?? id)
    return expandedInspectorIds.value.has(key)
  }

  function selectInspector(id: string) {
    params.selectInspectorImpl(id)
  }

  function toggleInspector(id: string) {
    const key = String(inspectorRowById(id)?.id ?? id)
    params.clearInspectorSidebarSelection()

    if (String(selectedInspectorId.value) === key && isInspectorExpanded(id)) {
      const next = new Set(expandedInspectorIds.value)
      next.delete(key)
      expandedInspectorIds.value = next
      selectedInspectorId.value = ''
      params.hideToast()
      params.rebuildInspectorAssignmentRows()
      return
    }

    selectInspector(id)
  }

  function clearInspectorSidebarSelection() {
    inspectorSidebarSelectedOrderId.value = ''
    inspectorSidebarSelectedLineIndex.value = null
  }

  const selectedInspector = computed(() => inspectorRowById(selectedInspectorId.value))

  const inspectorScheduleTitle = computed(() => {
    const name = selectedInspector.value?.name?.trim()
    return name ? `${name}'s schedule` : 'Schedule'
  })

  return {
    inspectors,
    selectedInspectorId,
    expandedInspectorIds,
    inspectorSidebarSelectedOrderId,
    inspectorSidebarSelectedLineIndex,
    selectedInspector,
    inspectorScheduleTitle,
    inspectorRowById,
    isSameInspectorId,
    applyInspectorRowsFromTechnicians,
    loadInspectors,
    ensureInspectorExpanded,
    isInspectorExpanded,
    selectInspector,
    toggleInspector,
    clearInspectorSidebarSelection,
    rebuildInspectorAssignmentRows: params.rebuildInspectorAssignmentRows,
  }
}
